// One event taking availability, on My events: say you're available (with notes)
// or withdraw. The server (core/event_availability.py) holds the rules — only
// nothing <-> Available here; past that, the status is the office's and only the
// notes can change, which `row.can_change` reports.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, TextField, Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RichTextView } from "./RichText";
import { API_BASE_URL } from "../config";
import { STATUS_COLOR } from "../constants";
import { notify } from "../notify";
import { useWriteLock } from "../useWriteLock";

const AVAILABILITY_API = `${API_BASE_URL}/attendances/availability`;
const CONTACT_EMAIL = "hello@shelter-stone.com";

/**
 * Props:
 *  - row:       AvailabilityRead ({event, status, availability_notes, can_change})
 *  - dateRange: (event) => string
 *  - blocked:   true while the record is missing something availability needs
 *  - onSaved:   (row) => void
 */
export default function AvailabilityCard({ row, dateRange, blocked, onSaved }) {
  const [notes, setNotes] = useState(row.availability_notes || "");
  const { busy, run } = useWriteLock();
  // The event's information, folded away until asked for: there may be several
  // events listed, and each can carry a page of briefing.
  const [showInfo, setShowInfo] = useState(false);
  const hasInfo = !!(row.event.information || "").trim();
  useEffect(() => { setNotes(row.availability_notes || ""); }, [row.availability_notes]);

  const available = row.status === "Available";
  const committed = !row.can_change; // Selected, a contract…
  const notesDirty = notes.trim() !== (row.availability_notes || "");

  const put = (isAvailable, message) => run(() =>
    axios.put(`${AVAILABILITY_API}/${row.event.id}`, { available: isAvailable, notes: notes.trim() || null })
      .then(res => { onSaved(res.data); notify(message, "success"); }));

  return (
    <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: available ? "success.main" : "divider" }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{row.event.label_long}</Typography>
            <Typography variant="body2" color="text.secondary">{dateRange(row.event)}</Typography>
          </Box>
          {row.status && (
            <Chip size="small" label={row.status} color={STATUS_COLOR[row.status] || "default"}
                  icon={available ? <CheckIcon /> : undefined} />
          )}
        </Stack>

        {hasInfo && (
          <Box>
            <Button size="small" onClick={() => setShowInfo(v => !v)} aria-expanded={showInfo}
                    startIcon={showInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ ml: -0.5 }}>
              {showInfo ? "Hide event information" : "Show event information"}
            </Button>
            {/* unmountOnExit: a collapsed briefing isn't rendered at all. */}
            <Collapse in={showInfo} unmountOnExit>
              <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <RichTextView value={row.event.information} dense />
              </Box>
            </Collapse>
          </Box>
        )}

        <TextField label="Availability notes" fullWidth multiline minRows={2} size="small"
                   value={notes} onChange={e => setNotes(e.target.value)}
                   slotProps={{ htmlInput: { maxLength: 2000 } }}
                   helperText="Anything we should know, e.g. travel constraints or the times you can do." />

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
          {committed ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
                You're {row.status} for this event. To withdraw, please contact {CONTACT_EMAIL}.
              </Typography>
              <Button size="small" variant="outlined" disabled={!notesDirty || busy}
                      onClick={() => put(true, "Notes saved.")}>
                Save notes
              </Button>
            </>
          ) : available ? (
            <>
              <Button size="small" color="error" disabled={busy}
                      onClick={() => put(false, "You're no longer down as available.")}>
                I'm no longer available
              </Button>
              <Button size="small" variant="outlined" disabled={!notesDirty || busy}
                      onClick={() => put(true, "Notes saved.")}>
                Save notes
              </Button>
            </>
          ) : (
            <Button size="small" variant="contained" disabled={blocked || busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                    onClick={() => put(true, "Thanks — you're down as available.")}>
              I'm available
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
